import { useEffect, useState } from "react";

/**
 * Tracks navigator.onLine with a small reconnect grace.
 *
 * Returns: {
 *   online,            // boolean — current state
 *   wasOffline,        // boolean — has been offline at least once this mount
 *   offlineSinceMs,    // number  — ms since the last offline transition (0 if online)
 * }
 *
 * Debounce-ish behaviour: we update state on every online/offline event,
 * but consumers can use wasOffline to render "reconnecting / restored"
 * messaging even after a brief flap.
 *
 * Used by interview-flow components per prompt #3: pause the recording
 * timer when offline, resume + flush pending chunks when back online,
 * surface a "Your progress is saved" banner.
 */
export default function useOnlineStatus() {
  const initial = typeof navigator !== "undefined" ? navigator.onLine : true;
  const [online, setOnline] = useState(initial);
  const [wasOffline, setWasOffline] = useState(!initial);
  const [offlineSince, setOfflineSince] = useState(initial ? 0 : Date.now());

  useEffect(() => {
    const handleOnline = () => {
      setOnline(true);
      setOfflineSince(0);
    };
    const handleOffline = () => {
      setOnline(false);
      setWasOffline(true);
      setOfflineSince(Date.now());
    };
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return {
    online,
    wasOffline,
    offlineSinceMs: offlineSince === 0 ? 0 : Date.now() - offlineSince,
  };
}
